/**
 * One donor's answer to the live appeal, remembered for this tab (GH-937).
 *
 * The appeal card must acknowledge a pledge or gift and stop asking for the rest of the session —
 * but the answer happens on the checkout page and the card lives on the event page, two documents
 * apart. `sessionStorage` is the narrowest thing that spans them: same tab, gone when the tab
 * closes, never sent anywhere, and holding nothing but an amount, a rung id and which kind of
 * answer it was. No donor identity, no donation or pledge id — the card only needs to know *that*
 * this tab answered, never *as whom*.
 *
 * Storage can refuse (private windows, cleared site data), so every read and write is wrapped and
 * the card simply asks again where nothing could be remembered — the server still refuses a
 * double-count nowhere, because answering twice is allowed; this is about not nagging.
 */
import type { MoneyWire } from "@givehere/contracts";

export type AppealResponse = {
  /** "pledged" raised a hand; "gave" paid through a money rail. */
  readonly kind: "pledged" | "gave";
  readonly rungId: string;
  readonly amount: MoneyWire;
};

const key = (eventId: string) => `givehere.appeal.answered.${eventId}`;

const MINOR_UNITS = /^[0-9]{1,15}$/;

/** True only for a value with exactly the remembered shape — storage contents are not trusted. */
function isAppealResponse(value: unknown): value is AppealResponse {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  const amount = record["amount"] as Record<string, unknown> | undefined;
  return (
    (record["kind"] === "pledged" || record["kind"] === "gave") &&
    typeof record["rungId"] === "string" &&
    record["rungId"].length > 0 &&
    typeof amount === "object" &&
    amount !== null &&
    typeof amount["amountMinor"] === "string" &&
    MINOR_UNITS.test(amount["amountMinor"]) &&
    typeof amount["currency"] === "string" &&
    amount["currency"].length === 3
  );
}

export function rememberAppealResponse(eventId: string, response: AppealResponse): void {
  try {
    window.sessionStorage.setItem(key(eventId), JSON.stringify(response));
  } catch {
    // Storage refused: the card will ask again, which costs a nag and nothing else.
  }
}

export function recalledAppealResponse(eventId: string): AppealResponse | null {
  try {
    const stored = window.sessionStorage.getItem(key(eventId));
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    return isAppealResponse(parsed) ? parsed : null;
  } catch {
    return null;
  }
}
